import * as React from "react"
import DatePicker, { registerLocale } from "react-datepicker"
import { FieldError } from "react-hook-form"
import { Box, Flex, FormControl, Input as CInput, InputProps } from "@chakra-ui/react"
import dayjs from "dayjs"
import "react-datepicker/dist/react-datepicker.css"
import { nl } from "date-fns/locale"
registerLocale("nl", nl)

import { useFormContext } from "lib/hooks/useForm"

import { InputError } from "./InputError"
import { InputLabel } from "./InputLabel"
interface DateInputProps extends Omit<InputProps, "name"> {
  name: string
  label?: string
  subLabel?: string
}

export const DateInput = (props: DateInputProps) => {
  const { register, unregister, watch, setValue } = useFormContext()
  const value = watch(props.name)

  const handleChange = (date: string | null) =>
    setValue(props.name, date ? dayjs(date).add(8, "hours").toISOString() : null, { shouldDirty: true })

  React.useEffect(() => {
    register(props.name)
    return () => unregister(props.name)
  }, [register, unregister, props.name])

  return (
    <Box pos="relative" w="100%">
      <DatePicker
        dateFormat="dd/MM/yyyy"
        dropdownMode="select"
        showMonthDropdown
        aria-label={`${props.label} date input`}
        showYearDropdown
        selected={value ? dayjs(value).startOf("day").toDate() : undefined}
        onChange={(date) => {
          handleChange(
            date
              ? dayjs(date as Date)
                  .startOf("day")
                  .format("YYYY-MM-DD")
              : null,
          )
        }}
        customInput={<CustomInput {...props} inputName={props.name} />}
        locale="nl"
        formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 2)} 
      />
    </Box>
  )
}

function _CustomInput({inputName, ...props}: DateInputProps & { inputName: string }, ref: React.Ref<HTMLInputElement>) {
  const {
    formState: { errors },
  } = useFormContext()
  const fieldError = errors?.[inputName] as FieldError | string
  return (
    <FormControl isInvalid={!!fieldError} mb={0} isRequired={props.isRequired}>
      <Flex align="center">
        <InputLabel label={props.label} subLabel={props.subLabel} name={inputName} />
      </Flex>
      <CInput id={inputName} mb={0} w="100%" ref={ref} {...props} aria-label={props.label ? `${props.label} date input` : undefined} />
      <InputError error={fieldError} />
    </FormControl>
  )
}

const CustomInput = React.forwardRef(_CustomInput)

export const ControlledDateInput = ({
  onChange,
  value,
  ...props
}: Omit<InputProps, "onChange"> & {
  onChange: (date: string) => void
  value: string
  label?: string
}) => {
  return (
    <DatePicker
      customInput={<CustomControlledDate {...props} />}
      dateFormat="dd/MM/yyyy"
      showYearDropdown
      showMonthDropdown
      dropdownMode="select"
      selected={value ? dayjs(value).startOf("day").toDate() : undefined}
      onChange={(date) => {
        date &&
          onChange(
            dayjs(date as Date)
              .startOf("day")
              .format(),
          )
      }}
    />
  )
}

interface CustomProps extends InputProps {
  label?: string
  subLabel?: string
}
function _CustomControlledDate(props: CustomProps, ref: React.Ref<HTMLInputElement>) {
  return (
    <FormControl>
      <InputLabel label={props.label} subLabel={props.subLabel} />
      <CInput mb={0} ref={ref} {...props} color="white" bg="gray.700"/>
    </FormControl>
  )
}

const CustomControlledDate = React.forwardRef(_CustomControlledDate)
